import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';

import FormTributos from '../components/FormTributos';
import { validarRegrasTributarias, resumoErros } from '../regrasTributarias';
import { resumoNcm, aplicarNcm, mensagemErro } from '../tributosApi';
import { SituacaoProdutoTag, formatarNcm, normalizarTributos } from '../util';

const txt = v => String(v === null || v === undefined ? '' : v).trim();
const mesmoPerfil = (produto, perfil) => txt(produto.cstVenda) === txt(perfil.cstVenda)
  && txt(produto.csosnVenda) === txt(perfil.csosnVenda)
  && txt(produto.cfopVenda) === txt(perfil.cfopVenda)
  && Number(produto.icmsAliquota || 0) === Number(perfil.icmsAliquota || 0);

/**
 * Alteração em massa: aplica a mesma tributação aos produtos selecionados de um NCM.
 * Os produtos alterados e conferidos são sincronizados com a Retaguarda pelo eloNFCeMonitor.
 */
export default function AlteracaoNcm({ onAlteracao }) {
  const navigate = useNavigate();

  const [ncm, setNcm] = useState('');
  const [resumo, setResumo] = useState(null);
  const [form, setForm] = useState(normalizarTributos(null));
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [marcarConferido, setMarcarConferido] = useState(true);
  const [confirmar, setConfirmar] = useState(false);
  const [selecionados, setSelecionados] = useState([]);

  const produtos = useMemo(() => (resumo && resumo.produtos) || [], [resumo]);
  const qtdSelecionados = selecionados.length;
  const todosSelecionados = produtos.length > 0 && qtdSelecionados === produtos.length;

  const alternar = id => setSelecionados(atual => (atual.includes(id) ? atual.filter(i => i !== id) : [...atual, id]));
  const selecionarTodos = marcar => setSelecionados(marcar ? produtos.map(p => p.id) : []);
  const selecionarNaoConferidos = () => setSelecionados(produtos.filter(p => !p.atualizado).map(p => p.id));
  const selecionarPerfil = perfil => setSelecionados(produtos.filter(p => mesmoPerfil(p, perfil)).map(p => p.id));

  const pesquisar = async e => {
    if (e) e.preventDefault();
    const codigo = ncm.replace(/\D/g, '');
    if (codigo.length !== 8) {
      toast.warn('Informe o NCM com 8 dígitos.');
      return;
    }
    setCarregando(true);
    try {
      const dados = await resumoNcm(codigo);
      setResumo(dados);
      setSelecionados((dados.produtos || []).map(p => p.id)); // por padrão, todos selecionados
      // Pré-preenche com a tributação mais frequente do NCM
      const base = normalizarTributos(dados.modelo);
      setForm({ ...base, id: null, codigoEan: '', descricao: '', cnpjCliente: '', registroProduto: null, ncm: codigo });
      if (!dados.totalProdutos) toast.info('Nenhum produto cadastrado com esse NCM.');
    } catch (err) {
      setResumo(null);
      toast.error(mensagemErro(err, 'Erro ao consultar o NCM.'));
    } finally {
      setCarregando(false);
    }
  };

  const abrirConfirmacao = () => {
    if (!qtdSelecionados) {
      toast.warn('Selecione ao menos um produto.');
      return;
    }
    const { erros } = validarRegrasTributarias(form);
    if (marcarConferido && erros.length) {
      toast.error(`Corrija a tributação antes de aplicar como conferido: ${resumoErros(erros)}`);
      return;
    }
    setConfirmar(true);
  };

  const aplicar = async () => {
    setSalvando(true);
    try {
      // Todos selecionados: não envia a lista (aplica ao NCM inteiro)
      const ids = todosSelecionados ? null : selecionados;
      const r = await aplicarNcm(resumo.ncm, form, marcarConferido, ids);
      toast.success(`Tributação aplicada a ${r.quantidade} produto(s). A Retaguarda será atualizada na próxima sincronização.`);
      setConfirmar(false);
      onAlteracao();
      await pesquisar();
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao aplicar a tributação.'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="tb-page">
      <div className="tb-page-head">
        <div>
          <h1>Alteração por NCM</h1>
          <p>Aplica a mesma tributação aos produtos selecionados de um NCM e sincroniza com a Retaguarda.</p>
        </div>
        <form className="tb-search-inline" onSubmit={pesquisar}>
          <i className="pi pi-search" />
          <input value={ncm} onChange={e => setNcm(e.target.value)} placeholder="NCM (8 dígitos)" inputMode="numeric" maxLength={10} />
          <button type="submit" className="tb-btn tb-btn-primary tb-btn-sm" disabled={carregando}>
            {carregando ? <i className="pi pi-spin pi-spinner" /> : 'Buscar'}
          </button>
        </form>
      </div>

      {resumo && resumo.totalProdutos > 0 ? (
        <div className="tb-split">
          <aside className="tb-card tb-side-info">
            <h2>NCM {formatarNcm(resumo.ncm)}</h2>
            <div className="tb-info-grid" style={{ marginTop: 12 }}>
              <div className="tb-info"><small>Produtos</small><span>{resumo.totalProdutos}</span></div>
              <div className="tb-info"><small>Conferidos</small><span>{resumo.conferidos}</span></div>
            </div>

            <h3>Tributações encontradas</h3>
            <ul className="tb-history">
              {resumo.perfis.map(p => (
                <li
                  key={`${p.cstVenda}|${p.csosnVenda}|${p.cfopVenda}|${p.icmsAliquota}`}
                  className="tb-perfil-selecionavel"
                  title="Selecionar os produtos com esta tributação"
                  onClick={() => selecionarPerfil(p)}
                >
                  <span className="tb-tag tb-tag-info">{p.quantidade}</span>
                  <span className="tb-mono">
                    {p.cstVenda ? `CST ${p.cstVenda}` : ''}{p.cstVenda && p.csosnVenda ? ' · ' : ''}{p.csosnVenda ? `CSOSN ${p.csosnVenda}` : ''}
                    {!p.cstVenda && !p.csosnVenda ? 'Sem CST/CSOSN' : ''}
                  </span>
                  <small>{p.cfopVenda ? `CFOP ${p.cfopVenda} · ` : ''}ICMS {Number(p.icmsAliquota || 0).toLocaleString('pt-BR')}%</small>
                </li>
              ))}
            </ul>

            <h3>Produtos <span className="tb-sel-contador">{qtdSelecionados} de {produtos.length} selecionado(s)</span></h3>
            <div className="tb-sel-barra">
              <span className="tb-sel-todos">
                <Checkbox inputId="selTodos" checked={todosSelecionados} onChange={e => selecionarTodos(e.checked)} />
                <label htmlFor="selTodos">Todos</label>
              </span>
              <button type="button" className="tb-btn tb-btn-link" onClick={selecionarNaoConferidos}>Não conferidos</button>
              <button type="button" className="tb-btn tb-btn-link" onClick={() => selecionarTodos(false)}>Limpar</button>
            </div>
            <ul className="tb-history tb-ncm-produtos tb-ncm-selecao">
              {produtos.map(p => (
                <li key={p.id} className={selecionados.includes(p.id) ? 'tb-sel-ativo' : ''}>
                  <Checkbox inputId={`sel${p.id}`} checked={selecionados.includes(p.id)} onChange={() => alternar(p.id)} />
                  <SituacaoProdutoTag produto={p} />
                  <button type="button" className="tb-btn tb-btn-link" onClick={() => navigate(`/tributos/produtos/${p.id}`)}>
                    {p.descricao}
                  </button>
                  <small className="tb-mono">{p.codigoEan || `Reg. ${p.registroProduto || '—'}`}</small>
                </li>
              ))}
            </ul>
          </aside>

          <div className="tb-card tb-form-card">
            <FormTributos valor={form} onChange={setForm} modoNcm />

            <div className="tb-actions">
              <div className="tb-actions-left">
                <Checkbox inputId="marcarConferido" checked={marcarConferido} onChange={e => setMarcarConferido(e.checked)} />
                <label htmlFor="marcarConferido" style={{ marginLeft: 8, cursor: 'pointer' }}>
                  Marcar como conferidos (envia para a Retaguarda)
                </label>
              </div>
              <div className="tb-actions-right">
                <button type="button" className="tb-btn tb-btn-primary" onClick={abrirConfirmacao} disabled={salvando || !qtdSelecionados}>
                  <i className="pi pi-check-circle" /> Aplicar a {qtdSelecionados} produto(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {!resumo && !carregando ? (
        <section className="tb-card">
          <div className="tb-empty">
            <i className="pi pi-sliders-h" />
            <span>Informe um NCM para ver os produtos e a tributação atual.</span>
          </div>
        </section>
      ) : null}

      <Dialog
        className="tb-dialog"
        header="Aplicar tributação ao NCM"
        visible={confirmar}
        onHide={() => setConfirmar(false)}
        style={{ width: '30rem', maxWidth: '95vw' }}
        footer={(
          <div>
            <button type="button" className="tb-btn tb-btn-ghost" onClick={() => setConfirmar(false)}>Cancelar</button>
            <button type="button" className="tb-btn tb-btn-primary" onClick={aplicar} disabled={salvando}>
              {salvando ? <i className="pi pi-spin pi-spinner" /> : null} Aplicar
            </button>
          </div>
        )}
      >
        {resumo ? (
          <p>
            A tributação informada substituirá a de <strong>{qtdSelecionados} produto(s)</strong>
            {todosSelecionados ? '' : ` (de ${produtos.length})`} do NCM{' '}
            <strong>{formatarNcm(resumo.ncm)}</strong>
            {marcarConferido ? ', que serão marcados como conferidos e sincronizados com a Retaguarda.' : '.'}
          </p>
        ) : null}
      </Dialog>
    </div>
  );
}

AlteracaoNcm.propTypes = {
  onAlteracao: PropTypes.func.isRequired,
};
